import React from "react";

import { Skeleton, Typography } from "@packages/ds";
import { useIntl } from "react-intl";
import { useLocation } from "react-router-dom";

import useDecodedParams from "Hooks/useDecodedParams";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import useUrls from "Hooks/useUrls";
import { roundedNumber } from "Libs/utils";
import { environmentSelector } from "Reducers/environment";
import {
  deploymentResourcesSelector,
  isLoadingSelector,
  isUpdateLoadingSelector
} from "Reducers/environment/deployment";
import { selectEnableGuaranteedResources } from "Reducers/project/capabilities";
import { gitProjectSelector } from "Reducers/project/project";
import { ConfigureResourcesButton } from "src/organization/pages/billing/projectBilling/components/ConfigureResourcesButton/ConfigureResourcesButton";
import { useAppSelector } from "Store/hooks";

import * as S from "./ResourcesBreakdown.styles";

const ResourcesBreakdown = () => {
  const location = useLocation();
  const intl = useIntl();
  const { organizationId, projectId, environmentId } = useDecodedParams<{
    organizationId: string;
    projectId: string;
    environmentId?: string;
  }>();

  const project = useSelectorWithUrlParams(gitProjectSelector);

  const envId = environmentId || project?.default_branch;

  const [resourcesUrl] = useUrls(
    "organization.project.environment.configureResources",
    {
      environmentId: envId || "",
      location: location.pathname + location.search,
      queryParams: ""
    }
  );

  const environment = useSelectorWithUrlParams(environmentSelector, {
    environmentId: envId
  });

  const resources = useSelectorWithUrlParams(deploymentResourcesSelector, {
    environmentId: envId
  });

  const isGuaranteedResourcesEnabled = useSelectorWithUrlParams(
    selectEnableGuaranteedResources
  );

  const isDeploymentLoading = useAppSelector(isLoadingSelector);
  const isDeploymentUpdateLoading = useAppSelector(isUpdateLoadingSelector);

  const hasValidResources =
    resources.cpu.total || resources.ram || resources.disk;

  const showLoading =
    isDeploymentLoading ||
    isDeploymentUpdateLoading ||
    typeof isGuaranteedResourcesEnabled === "undefined";

  const isEnvironmentOverviewPage = !!environmentId;

  if (!hasValidResources) {
    return null;
  }

  const showEnvironmentNameSkeleton =
    !environment?.title && !isEnvironmentOverviewPage;
  const showEnvironmentName = environment?.title && !isEnvironmentOverviewPage;

  let rows: React.ReactNode;
  if (isGuaranteedResourcesEnabled) {
    rows = showLoading ? (
      <Skeleton synchronize>
        <S.SkeletonCard>
          <Skeleton.Square height="30px" />
          <Skeleton.Square height="30px" />
        </S.SkeletonCard>
        <S.SkeletonCard>
          <Skeleton.Square height="30px" />
          <Skeleton.Square height="30px" />
        </S.SkeletonCard>
      </Skeleton>
    ) : (
      <S.Rows>
        <S.Row>
          <S.Group>
            <S.ItemTop weight="semiBold">{resources.cpu.shared}</S.ItemTop>
            <S.ItemBottom size="small" weight="semiBold">
              {intl.formatMessage({ id: "resources.breakdown.cpu.shared" })}
            </S.ItemBottom>
          </S.Group>
          <S.Group>
            <S.ItemTop weight="semiBold">{resources.cpu.guaranteed}</S.ItemTop>
            <S.ItemBottom size="small" weight="semiBold">
              {intl.formatMessage({
                id: "resources.breakdown.cpu.guaranteed"
              })}
            </S.ItemBottom>
          </S.Group>
        </S.Row>

        <S.Row>
          <S.Group>
            <S.ItemTop weight="semiBold">
              {intl.formatMessage(
                {
                  id: "project.details.double_value"
                },
                {
                  value: roundedNumber(resources.ram, 1),
                  separator: () => " ",
                  value2: "GB"
                }
              )}
            </S.ItemTop>
            <S.ItemBottom size="small" weight="semiBold">
              {intl.formatMessage({ id: "resources.breakdown.ram" })}
            </S.ItemBottom>
          </S.Group>
          <S.Group>
            <S.ItemTop weight="semiBold">
              {intl.formatMessage(
                {
                  id: "project.details.double_value"
                },
                {
                  value: resources.disk,
                  separator: () => " ",
                  value2: "GB"
                }
              )}
            </S.ItemTop>
            <S.ItemBottom size="small" weight="semiBold">
              {intl.formatMessage({ id: "resources.breakdown.disk" })}
            </S.ItemBottom>
          </S.Group>
        </S.Row>
      </S.Rows>
    );
  } else {
    // To be deleted once git rolls out guaranteed resources to all regions
    rows = showLoading ? (
      <Skeleton synchronize>
        <S.SkeletonCard>
          <Skeleton.Square height="30px" />
          <Skeleton.Square height="30px" />
          <Skeleton.Square height="30px" />
        </S.SkeletonCard>
      </Skeleton>
    ) : (
      <S.Rows>
        <S.LegacyRow>
          <S.Group>
            <S.ItemTop weight="semiBold">{resources.cpu.shared}</S.ItemTop>
            <S.ItemBottom size="small" weight="semiBold">
              {intl.formatMessage({
                id: "resources.breakdown.cpu"
              })}
            </S.ItemBottom>
          </S.Group>

          <S.Group>
            <S.ItemTop weight="semiBold">
              {intl.formatMessage(
                {
                  id: "project.details.double_value"
                },
                {
                  value: roundedNumber(resources.ram, 1),
                  separator: () => " ",
                  value2: "GB"
                }
              )}
            </S.ItemTop>
            <S.ItemBottom size="small" weight="semiBold">
              {intl.formatMessage({ id: "resources.breakdown.ram" })}
            </S.ItemBottom>
          </S.Group>

          <S.Group>
            <S.ItemTop weight="semiBold">
              {intl.formatMessage(
                {
                  id: "project.details.double_value"
                },
                {
                  value: resources.disk,
                  separator: () => " ",
                  value2: "GB"
                }
              )}
            </S.ItemTop>
            <S.ItemBottom size="small" weight="semiBold">
              {intl.formatMessage({ id: "resources.breakdown.disk" })}
            </S.ItemBottom>
          </S.Group>
        </S.LegacyRow>
      </S.Rows>
    );
  }

  return (
    <S.ListGroup>
      <S.TopRow smallMargin={!environmentId}>
        <Typography.Title tag="h3">
          {intl.formatMessage({ id: "resources.breakdown.title" })}
        </Typography.Title>
        <ConfigureResourcesButton
          environmentConfigUrl={resourcesUrl}
          organizationId={organizationId}
          projectId={projectId}
          environmentId={envId!}
        />
      </S.TopRow>

      {showEnvironmentNameSkeleton && (
        <Skeleton synchronize>
          <S.SkeletonCardEnvName>
            <Skeleton.Square height="30px" width="50%" />
          </S.SkeletonCardEnvName>
        </Skeleton>
      )}

      {showEnvironmentName && (
        <S.SubTitle weight="semiBold">{environment.title}</S.SubTitle>
      )}

      {rows}
    </S.ListGroup>
  );
};

export default ResourcesBreakdown;
